import { CSSProperties, useMemo } from 'react';

import { ExpandedState, OnChangeFn, Row } from '@tanstack/react-table';

import { Dimensions } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import DataTable from 'components/shared/DataTable';

import {
  buildReferralColumns,
  buildReferralRows,
  EntityCellRenderer,
  ReferralColumnKey,
  ReferralTableRow,
} from './referralDetailsColumns';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  dimension: Dimensions;
  entityHeader: string;
  entityCell?: EntityCellRenderer;
  visibleColumns?: readonly ReferralColumnKey[];
  maxHeight?: string;
  rows?: ReferralTableRow[];
  rowStyle?: (row: Row<ReferralTableRow>) => CSSProperties | undefined;
  defaultSort?: { id: string; desc: boolean };
  getSubRows?: (row: ReferralTableRow) => ReferralTableRow[] | undefined;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
};

const DEFAULT_SORT = [{ id: 'referrals', desc: true }];

export default function ReferralDetailsTable({
  loading,
  rollups,
  dimension,
  entityHeader,
  entityCell,
  visibleColumns,
  maxHeight = '520px',
  rows,
  rowStyle,
  defaultSort,
  getSubRows,
  expanded,
  onExpandedChange,
}: Props) {
  // Dashboard always queries with [entity, status] = 2 dimensions.
  const derivedRows = useMemo(() => buildReferralRows(rollups, 2), [rollups]);
  const tableData = rows ?? derivedRows;
  const columns = useMemo(
    () => buildReferralColumns({ dimension, entityHeader, entityCell, visible: visibleColumns }),
    [dimension, entityHeader, entityCell, visibleColumns]
  );

  const sortBy = defaultSort ? [defaultSort] : DEFAULT_SORT;

  return (
    <DataTable
      columns={columns}
      data={tableData}
      defaultSortBy={sortBy}
      loading={loading}
      hasFooter={false}
      maxHeight={maxHeight}
      rowStyle={rowStyle}
      freezeFirstColumn
      getSubRows={getSubRows}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    />
  );
}
